import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import type { PaginationDTO, SearchProductDataDTO, SortingOptionDTO } from "~/features/search/models/SearchResultsDTO";
import { type SortDirection } from "~/features/search/models/models";
import { getBESortCode } from "~/features/search/redux/searchSelectors";
import type { DeprecatedOccPaginationDTO } from "~/app/api/occ/DeprecatedOccPaginationDTO";

function getApiUrl(): string {
  return occUrl("/fcc/search");
}

export interface FCCSearchResultsDTO {
  results: SearchProductDataDTO[];
  pagination: PaginationDTO;
  sorts: SortingOptionDTO[];
  categoryPath: FCCTreeNodeDTO[];
  productHierarchy: FCCTreeNodeDTO[];
  plantHierarchy: FCCTreeNodeDTO[];
}

export interface FCCSearchResultsResponseDTO {
  results?: SearchProductDataDTO[];
  pagination: DeprecatedOccPaginationDTO;
  sorts?: SortingOptionDTO[];
  categoryPath?: FCCRawTreeNodeDTO[];
  productHierarchy?: FCCRawTreeNodeDTO[];
  plantHierarchy?: FCCRawTreeNodeDTO[];
}

export interface FCCTreeNodeDTO {
  name: string;
  code: string;
  count: number;
  children: FCCTreeNodeDTO[];
}

interface FCCRawTreeNodeDTO extends Omit<FCCTreeNodeDTO, "children"> {
  children?: FCCRawTreeNodeDTO[];
}

interface SearchParams {
  query?: string;
  categoryPath?: string;
  productHierarchy?: string;
  plantHierarchy?: string;
  page?: number;
  relevantProducts?: boolean;
  sortCode?: string;
  sortDirection?: SortDirection;
}

const FACETS: (keyof SearchParams)[] = ["categoryPath", "productHierarchy", "plantHierarchy"];

export async function fetchFCCSearchResults(
  data: SearchParams,
  abortSignal?: AbortSignal,
): Promise<FCCSearchResultsDTO> {
  const params = new URLSearchParams();
  if (data.query) {
    params.set("text", data.query);
  }
  if (data.page) {
    params.set("page", data.page.toString());
  }
  if (data.relevantProducts) {
    params.set("relevantProducts", "true");
  }
  const qParts: string[] = [];
  qParts.push(data.query ?? ""); // Search string

  qParts.push(getBESortCode(data.sortCode ?? null, data.sortDirection ?? null) ?? "null"); // Sorting
  FACETS.forEach((facet) => {
    if (data[facet]) {
      qParts.push(`${facet}:${data[facet]}`);
    }
  });
  params.set("q", qParts.join(":"));

  const response = await occFetch(getApiUrl() + "?" + params.toString(), {
    signal: abortSignal,
  });

  if (response.redirected) {
    window.location.reload();
  }

  if (!response.ok) {
    throw new Error("Error fetching search results");
  }

  const dto = (await response.json()) as FCCSearchResultsResponseDTO;
  return {
    results: dto.results ?? [],
    sorts: dto.sorts ?? [],
    categoryPath: normalizeTree(dto.categoryPath),
    productHierarchy: normalizeTree(dto.productHierarchy),
    plantHierarchy: normalizeTree(dto.plantHierarchy),
    pagination: {
      pageSize: dto.pagination.pageSize,
      currentPage: dto.pagination.currentPage,
      sort: dto.pagination.sort,
      numberOfPages: dto.pagination.totalPages,
      totalNumberOfResults: dto.pagination.totalResults,
    },
  };
}

function normalizeTree(nodes: FCCRawTreeNodeDTO[] | null | undefined): FCCTreeNodeDTO[] {
  return (nodes ?? []).map((node) => ({
    ...node,
    children: normalizeTree(node.children),
  }));
}
